/**
 * Several properties, one loan.
 *
 * A construction request often arrives as N properties underwritten
 * separately and financed as one loan. This panel holds one assumption set
 * per property — its figures editable in place, or read from the sheet of
 * the sponsor's model that states them — and shows the deal-level roll-up
 * beneath: sums where a field is additive, ratios re-derived from the summed
 * legs. "Apply to deal" writes the roll-up onto the record through the same
 * derivation path a single-property deal uses.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, FileSpreadsheet, Loader2, Plus, Sigma, Trash2 } from 'lucide-react'
import {
  assumptionSetsService,
  type AssumptionField,
  type AssumptionSet,
  type AssumptionSetsPayload,
} from '@/services/assumptionSets'
import { invalidateLoanQueries } from '@/services/loanQueryKeys'
import { formatFieldValue } from './format'

const DASH = '—'

/** The fields shown as columns by default; the rest are behind "more". */
const DEFAULT_COLUMNS = [
  'units', 'sf', 'total_cost', 'property_value', 'noi', 'noi_stabilized', 'occupancy', 'cap_rate',
]

export const ASSUMPTION_SETS_KEY = (loanId: string) => ['loan-assumption-sets', loanId] as const

function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}

function parseNumber(text: string): number | null {
  const cleaned = text.replace(/[$,%\s]/g, '').replace(/x$/i, '')
  if (cleaned === '' || cleaned === '-') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

export function AssumptionSetsPanel({
  loanId,
  unlocked,
  modelVersionId,
  modelFilename,
}: {
  loanId: string
  unlocked: boolean
  /** The selected model, so its sheets can be read as properties. */
  modelVersionId: string | null
  modelFilename: string | null
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [moreColumns, setMoreColumns] = useState(false)
  const [newName, setNewName] = useState('')
  const [drafts, setDrafts] = useState<Record<string, string>>({})   // `${setId}:${field}` → text

  const query = useQuery({
    queryKey: ASSUMPTION_SETS_KEY(loanId),
    queryFn: () => assumptionSetsService.list(loanId),
    enabled: Boolean(loanId),
    staleTime: 30_000,
  })
  const data = query.data

  useEffect(() => {
    setDrafts({})
  }, [data?.sets])

  const settle = (payload: AssumptionSetsPayload) => {
    setError(null)
    queryClient.setQueryData(ASSUMPTION_SETS_KEY(loanId), payload)
  }
  const fail = (fallback: string) => (err: unknown) => setError(errorDetail(err, fallback))

  const create = useMutation({
    mutationFn: (name: string) => assumptionSetsService.create(loanId, { name }),
    onSuccess: (payload) => {
      settle(payload)
      setNewName('')
    },
    onError: fail('The property could not be added.'),
  })
  const withdraw = useMutation({
    mutationFn: (setId: string) => assumptionSetsService.withdraw(loanId, setId),
    onSuccess: settle,
    onError: fail('The property could not be removed.'),
  })
  const putValues = useMutation({
    mutationFn: ({ setId, values }: { setId: string; values: { field: string; value: number | null }[] }) =>
      assumptionSetsService.putValues(loanId, setId, values),
    onSuccess: settle,
    onError: fail('The figure could not be saved.'),
  })
  const rename = useMutation({
    mutationFn: ({ setId, name }: { setId: string; name: string }) => assumptionSetsService.update(loanId, setId, { name }),
    onSuccess: settle,
    onError: fail('The property could not be renamed.'),
  })
  const fromWorkbook = useMutation({
    mutationFn: (versionId: string) => assumptionSetsService.fromWorkbook(loanId, versionId),
    onSuccess: (payload) => {
      settle(payload)
      if (!payload.created?.length) {
        setError('No sheet of that model states a property on its own — nothing was added.')
      }
    },
    onError: fail('The model could not be read per sheet.'),
  })
  const apply = useMutation({
    mutationFn: () => assumptionSetsService.apply(loanId),
    onSuccess: async (payload) => {
      settle(payload)
      await invalidateLoanQueries(queryClient, loanId)
    },
    onError: fail('The roll-up could not be applied to the deal.'),
  })

  const fields = useMemo(() => data?.fields ?? [], [data?.fields])
  const columns = useMemo(() => {
    const byName = new Map(fields.map((f) => [f.name, f]))
    const stated = new Set((data?.sets ?? []).flatMap((s) => s.values.map((v) => v.field)))
    const primary = DEFAULT_COLUMNS.filter((name) => byName.has(name))
    const extra = fields.filter((f) => !DEFAULT_COLUMNS.includes(f.name) && (moreColumns || stated.has(f.name)))
    return [...primary.map((name) => byName.get(name)!), ...extra]
  }, [fields, data?.sets, moreColumns])

  if (query.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-[9.5px] text-stone-500">
        <Loader2 size={10} className="animate-spin" /> Reading properties…
      </p>
    )
  }
  if (query.isError) {
    return <p className="text-[9.5px] text-red-300">{errorDetail(query.error, 'Property assumptions could not be loaded.')}</p>
  }
  const sets = data?.sets ?? []
  const rollup = data?.rollup ?? {}
  const busy = create.isPending || withdraw.isPending || putValues.isPending || fromWorkbook.isPending || apply.isPending

  const commit = (set: AssumptionSet, field: AssumptionField, text: string) => {
    const key = `${set.id}:${field.name}`
    const current = set.values.find((v) => v.field === field.name)?.value ?? null
    const next = parseNumber(text)
    if (next === current) {
      setDrafts((d) => {
        const copy = { ...d }
        delete copy[key]
        return copy
      })
      return
    }
    putValues.mutate({ setId: set.id, values: [{ field: field.name, value: next }] })
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[9.5px] leading-relaxed text-stone-500">
          {sets.length === 0
            ? 'One set per property, rolling up to this one loan. Add a property, or read them from the sheets of the selected model.'
            : `${sets.length} propert${sets.length === 1 ? 'y' : 'ies'} rolling up to this loan. Sums add; ratios are re-derived from the summed legs, never averaged.`}
        </p>
        <div className="flex items-center gap-1.5">
          <input
            aria-label="New property name"
            value={newName}
            disabled={!unlocked || busy}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newName.trim()) create.mutate(newName.trim())
            }}
            placeholder="Property name"
            className="w-36 rounded border border-stone-700 bg-[#0c0c0c] px-2 py-1 text-[9.5px] text-stone-200 outline-none focus:border-amber-300/50 disabled:opacity-50"
          />
          <button
            type="button"
            disabled={!unlocked || busy || !newName.trim()}
            onClick={() => create.mutate(newName.trim())}
            title={unlocked ? 'Add a property to this deal' : 'Unlock the dossier to add a property'}
            className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[9.5px] text-amber-200 transition-colors hover:bg-amber-400/[0.07] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
          >
            <Plus size={10} /> Add property
          </button>
          {modelVersionId ? (
            <button
              type="button"
              disabled={!unlocked || busy}
              onClick={() => fromWorkbook.mutate(modelVersionId)}
              title={modelFilename ? `Read one property per sheet from ${modelFilename}` : 'Read properties from the model'}
              className="inline-flex items-center gap-1 rounded border border-stone-700 px-2 py-1 text-[9.5px] text-stone-300 transition-colors hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:text-stone-600"
            >
              {fromWorkbook.isPending ? <Loader2 size={10} className="animate-spin" /> : <FileSpreadsheet size={10} />}
              Read from model
            </button>
          ) : null}
        </div>
      </div>

      {sets.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[9.5px]" aria-label="Property assumption sets">
            <thead>
              <tr className="text-left text-[8px] uppercase tracking-wide text-stone-600">
                <th className="pb-1 pr-2 font-medium">Property</th>
                <th className="pb-1 pr-2 text-right font-medium">Loan slice</th>
                {columns.map((f) => (
                  <th key={f.name} className="pb-1 pr-2 text-right font-medium" title={f.rolledUp ? 'Rolls up to the deal' : 'Stated per property only'}>
                    {f.label}
                  </th>
                ))}
                <th className="pb-1" />
              </tr>
            </thead>
            <tbody>
              {sets.map((set) => (
                <tr key={set.id} className="border-t border-stone-900 align-middle" data-set={set.id}>
                  <td className="py-1 pr-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <Building2 size={10} className="shrink-0 text-stone-600" />
                      <input
                        aria-label={`Name of ${set.name}`}
                        defaultValue={set.name}
                        disabled={!unlocked}
                        onBlur={(event) => {
                          const name = event.target.value.trim()
                          if (name && name !== set.name) rename.mutate({ setId: set.id, name })
                        }}
                        className="w-32 bg-transparent text-center text-stone-200 outline-none focus:border-b focus:border-amber-300/50 disabled:text-stone-300"
                      />
                    </div>
                    {set.sourceSheet ? (
                      <div className="pl-4 text-[8px] text-stone-600" title={set.workbookVersionId ?? undefined}>sheet {set.sourceSheet}</div>
                    ) : null}
                  </td>
                  <td className="py-1 pr-2 text-right">
                    <ValueCell
                      set={set}
                      field={{ name: 'loan_amount', label: 'Loan slice', kind: 'currency', unit: 'USD', group: 'The Request', rolledUp: true }}
                      value={set.loanAllocation}
                      unlocked={unlocked}
                      draft={drafts[`${set.id}:loan_amount`]}
                      onDraft={(text) => setDrafts((d) => ({ ...d, [`${set.id}:loan_amount`]: text }))}
                      onCommit={(text) => commit(set, { name: 'loan_amount', label: 'Loan slice', kind: 'currency', unit: 'USD', group: 'The Request', rolledUp: true }, text)}
                    />
                  </td>
                  {columns.map((f) => (
                    <td key={f.name} className="py-1 pr-2 text-right">
                      <ValueCell
                        set={set}
                        field={f}
                        value={set.values.find((v) => v.field === f.name)?.value ?? null}
                        source={set.values.find((v) => v.field === f.name)?.source ?? null}
                        unlocked={unlocked}
                        draft={drafts[`${set.id}:${f.name}`]}
                        onDraft={(text) => setDrafts((d) => ({ ...d, [`${set.id}:${f.name}`]: text }))}
                        onCommit={(text) => commit(set, f, text)}
                      />
                    </td>
                  ))}
                  <td className="py-1 text-right">
                    <button
                      type="button"
                      disabled={!unlocked || busy}
                      onClick={() => withdraw.mutate(set.id)}
                      aria-label={`Remove ${set.name}`}
                      title={unlocked ? 'Remove this property from the deal' : 'Unlock the dossier to remove a property'}
                      className="rounded p-0.5 text-stone-600 hover:text-red-300 disabled:cursor-not-allowed disabled:hover:text-stone-600"
                    >
                      <Trash2 size={10} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr className="border-t border-amber-400/30 bg-amber-400/[0.03] font-medium" data-testid="assumption-rollup">
                <td className="py-1.5 pr-2 text-amber-200">
                  <span className="inline-flex items-center gap-1"><Sigma size={10} /> Deal roll-up</span>
                </td>
                <td className="py-1.5 pr-2 text-right font-mono text-stone-100" title={rollup.loan_amount?.method}>
                  {rollup.loan_amount ? formatFieldValue(rollup.loan_amount.value, 'currency') : DASH}
                </td>
                {columns.map((f) => (
                  <td key={f.name} className="py-1.5 pr-2 text-right font-mono text-stone-100" title={rollup[f.name]?.method}>
                    {rollup[f.name] ? formatFieldValue(rollup[f.name].value, f.kind, f.name) : DASH}
                  </td>
                ))}
                <td />
              </tr>
            </tbody>
          </table>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            <div className="text-[8.5px] text-stone-600">
              {Object.values(rollup).slice(0, 4).map((r) => (
                <span key={r.field} className="mr-3">
                  <span className="text-stone-500">{fields.find((f) => f.name === r.field)?.label ?? r.field}:</span> {r.method}
                </span>
              ))}
              <button type="button" onClick={() => setMoreColumns((v) => !v)} className="text-amber-300/80 hover:underline">
                {moreColumns ? 'fewer fields' : 'more fields'}
              </button>
            </div>
            <button
              type="button"
              disabled={!unlocked || busy}
              onClick={() => apply.mutate()}
              title={unlocked ? 'Write the roll-up onto the deal record and re-run derivations' : 'Unlock the dossier to apply the roll-up'}
              className="inline-flex items-center gap-1 rounded border border-amber-400/30 px-2 py-1 text-[9.5px] text-amber-200 transition-colors hover:bg-amber-400/[0.09] disabled:cursor-not-allowed disabled:border-stone-800 disabled:text-stone-600"
            >
              {apply.isPending ? <Loader2 size={10} className="animate-spin" /> : <Sigma size={10} />}
              Apply roll-up to deal
            </button>
          </div>
        </div>
      ) : null}

      {(data?.warnings ?? []).map((w) => (
        <p key={w} className="text-[9.5px] text-amber-300">{w}</p>
      ))}
      {apply.data?.written?.length ? (
        <p className="text-[9.5px] text-emerald-300">
          Applied to the deal: {apply.data.written.join(', ')}.
        </p>
      ) : null}
      {error ? <p className="text-[9.5px] text-red-300">{error}</p> : null}
    </div>
  )
}

function ValueCell({
  set,
  field,
  value,
  source,
  unlocked,
  draft,
  onDraft,
  onCommit,
}: {
  set: AssumptionSet
  field: AssumptionField
  value: number | null
  source?: string | null
  unlocked: boolean
  draft: string | undefined
  onDraft: (text: string) => void
  onCommit: (text: string) => void
}) {
  const shown = draft ?? (value === null ? '' : String(value))
  return (
    <input
      aria-label={`${field.label} for ${set.name}`}
      value={shown}
      disabled={!unlocked}
      inputMode="decimal"
      title={source ? `From ${source}` : field.rolledUp ? 'Rolls up to the deal' : 'Stated per property only'}
      placeholder={DASH}
      onChange={(event) => onDraft(event.target.value)}
      onBlur={(event) => onCommit(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur()
      }}
      className={`w-24 rounded border bg-transparent px-1 py-0.5 text-center font-mono text-[9.5px] outline-none focus:border-amber-300/50 disabled:border-transparent ${
        value === null ? 'border-stone-900 text-stone-500' : 'border-stone-800 text-stone-200'
      } ${source && source !== 'manual' ? 'text-emerald-200' : ''}`}
    />
  )
}
